import { useEffect, useRef, useCallback } from 'react';
import { getFullUrl } from 'hubspot-url-utils';
import { openOAuthWindow } from '../../../utils/openOAuthWindow';
import { oAuthRedirectOrigin } from '../../../utils/oauthHelpers';
const SESSION_TIMEOUT_MS = 300000;
const HEARTBEAT_INTERVAL_MS = 1000;
export function useOAuthWindow({
  appName,
  callbacks,
  enabled
}) {
  const windowRef = useRef(null);
  const oAuthStateRef = useRef('');
  const sessionTimeoutRef = useRef(undefined);
  const heartbeatRef = useRef(undefined);
  const clearTimers = useCallback(() => {
    if (sessionTimeoutRef.current) {
      clearTimeout(sessionTimeoutRef.current);
      sessionTimeoutRef.current = undefined;
    }
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = undefined;
    }
  }, []);
  const closeWindow = useCallback(() => {
    clearTimers();
    if (windowRef.current) {
      windowRef.current.close();
      windowRef.current = null;
    }
    oAuthStateRef.current = '';
  }, [clearTimers]);
  const closeWindowRef = useRef(closeWindow);
  closeWindowRef.current = closeWindow;
  const handleAuthResponse = useCallback((status, params) => {
    const stateParam = params.get('state');
    if (!stateParam) {
      callbacks.onAuthError('Missing state parameter');
      return;
    }
    if (stateParam !== oAuthStateRef.current) {
      callbacks.onAuthError('State mismatch');
      return;
    }
    if (status === 'AUTH_ERROR') {
      callbacks.onAuthError(params.get('message') || undefined);
    } else if (status === 'AUTH_RESPONSE') {
      callbacks.onAuthResponse(params, status);
    } else {
      callbacks.onAuthError('Unknown OAuth response');
    }
    clearTimers();
    oAuthStateRef.current = '';
  }, [callbacks, clearTimers]);
  const handleAuthResponseRef = useRef(handleAuthResponse);
  handleAuthResponseRef.current = handleAuthResponse;
  const openWindow = useCallback((oAuthState, authorizeUrl, oAuthWindow) => {
    const resolvedWindow = openOAuthWindow(appName, oAuthWindow);
    if (!resolvedWindow) {
      callbacks.onWindowBlocked();
      return;
    }
    windowRef.current = resolvedWindow;
    oAuthStateRef.current = oAuthState;
    resolvedWindow.location = authorizeUrl;
    callbacks.onWindowOpened(resolvedWindow, oAuthState);
    sessionTimeoutRef.current = window.setTimeout(() => {
      callbacks.onWindowTimeout();
      closeWindow();
    }, SESSION_TIMEOUT_MS);
    heartbeatRef.current = window.setInterval(() => {
      if (!windowRef.current || windowRef.current.closed) {
        callbacks.onWindowClosed();
        clearTimers();
      }
    }, HEARTBEAT_INTERVAL_MS);
  }, [appName, callbacks, closeWindow, clearTimers]);
  useEffect(() => {
    if (!enabled) {
      return undefined;
    }
    const messageHandler = event => {
      const oauthWindow = windowRef.current;
      if (!event.data || !oauthWindow || event.source !== oauthWindow) {
        return;
      }
      if ((event.origin === oAuthRedirectOrigin || event.origin === getFullUrl('app')) && event.data === 'ping') {
        oauthWindow.postMessage('pong', event.origin);
        return;
      }
      if (event.origin === oAuthRedirectOrigin && event.data.queryString) {
        const oAuthRedirectSearchParams = new URLSearchParams(event.data.queryString);
        handleAuthResponseRef.current(event.data.type, oAuthRedirectSearchParams);
        oauthWindow.close();
        windowRef.current = null;
      }
    };
    window.addEventListener('message', messageHandler);
    return () => {
      window.removeEventListener('message', messageHandler);
      clearTimers();
    };
  }, [enabled, clearTimers]);
  useEffect(() => {
    return () => {
      closeWindowRef.current();
    };
  }, []);
  return {
    openWindow,
    closeWindow,
    isWindowOpen: windowRef.current !== null && !windowRef.current.closed
  };
}